// ZoomStage — a picture on a stage: zoom · pan · swipe, per COMPONENTS §Catalog.
// The image half of the files library's file surface (ADR-068 · FRONTEND-
// PATTERNS §Files). NATIVE-OWNED, hand-rolled (ADR-028 / ADR-014 — no image
// library, no gesture dependency): pointer events carry the drag, the pinch
// (two pointers) and the swipe; the wheel zooms around the pointer; the keyboard
// zooms and pans; a double-tap toggles a close look. The geometry is pure and
// unit-tested in zoomStageMath.ts.
//
// The stage owns ONLY the picture's viewport: what it renders is the caller's
// (a signed link), what a swipe does is the caller's (`onSwipePrev/Next` walk
// the lens the person came from), and the ground is the caller's box (paint it
// with the stage token). At rest (scale 1) a horizontal drag reads as a swipe;
// zoomed, the same drag pans — the two never fight because the threshold rule
// lives in swipeIntent.
//
// a11y: the gesture surface IS a button (the picture's own control — native
// focus, the keyboard model for free), named by the picture's text and
// described by sr-only instructions (+ − 0 · the arrows); the three buttons
// beside it (zoom out · zoom in · reset) are the non-pointer path to every
// gesture (WCAG 2.1.1 / 2.5.1 — a pinch always has a single-pointer + keyboard
// equivalent).

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { icons } from './icons.js';
import { IconButton } from './IconButton.js';
import {
  clampPan,
  clampScale,
  distance,
  fitContain,
  midpoint,
  swipeIntent,
  zoomAround,
  type Point,
  type Size,
} from './zoomStageMath.js';

export interface ZoomStageProps {
  src: string;
  /** The picture's text — names the stage for assistive tech. */
  alt: string;
  /** A swipe (or ←/→ at rest) walks the lens the person came from. Omitted = no swipe. */
  onSwipePrev?: () => void;
  onSwipeNext?: () => void;
  maxScale?: number;
  /** Opens already zoomed — workbench open-state stories (COMPONENTS §Workbench). */
  defaultScale?: number;
  /** The picture failed to load (a signed link that died) — the caller re-mints once, then shows the glyph. */
  onError?: () => void;
  /** Visible + sr strings are props (i18n-agnostic, like FileUpload). */
  zoomInLabel?: string;
  zoomOutLabel?: string;
  resetLabel?: string;
  instructions?: string;
  className?: string;
}

const ZOOM_STEP = 1.25;
const CLOSE_LOOK = 2.5;
const PAN_STEP_PX = 48;
const REST: Point = { x: 0, y: 0 };

export function ZoomStage({
  src,
  alt,
  onSwipePrev,
  onSwipeNext,
  maxScale = 6,
  defaultScale = 1,
  onError,
  zoomInLabel = 'Zoom in',
  zoomOutLabel = 'Zoom out',
  resetLabel = 'Reset zoom',
  instructions = 'Plus and minus zoom, 0 resets, arrow keys pan when zoomed and move between pictures at rest.',
  className = '',
}: ZoomStageProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const instructionsId = useId();
  const [box, setBox] = useState<Size>({ width: 0, height: 0 });
  const [natural, setNatural] = useState<Size>({ width: 0, height: 0 });
  const [view, setView] = useState<{ scale: number; pan: Point }>({ scale: clampScale(defaultScale, 1, maxScale), pan: REST });
  const [dragging, setDragging] = useState(false);

  // A new picture arrives at rest — adjusted DURING render with a prev-value
  // guard (never setState in an effect: a one-frame flash of the old pan on
  // the new picture, and a cascading render).
  const [prevSrc, setPrevSrc] = useState(src);
  if (prevSrc !== src) {
    setPrevSrc(src);
    setView({ scale: clampScale(defaultScale, 1, maxScale), pan: REST });
    setNatural({ width: 0, height: 0 });
  }

  const fitted = fitContain(natural, box);
  const bounds = { min: 1, max: maxScale, fitted, box };
  // The pan is re-clamped as a DERIVATION (a rotate, a late load) — never stored twice.
  const pan = clampPan(view.pan, fitted, box, view.scale);
  const zoomed = view.scale > 1.001;

  // Measure the box (one ResizeObserver, like DataTable's fit engine) — an
  // external system, the one legitimate setState-from-effect.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** Pointer position relative to the box CENTRE (the geometry's origin). */
  const centred = useCallback((clientX: number, clientY: number): Point => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r) return REST;
    return { x: clientX - (r.left + r.width / 2), y: clientY - (r.top + r.height / 2) };
  }, []);

  const zoomBy = useCallback(
    (factor: number, at: Point = REST) => setView((v) => zoomAround(v, factor, at, bounds)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bounds is a fresh object each render; its inputs are the deps
    [fitted.width, fitted.height, box.width, box.height, maxScale],
  );
  const reset = useCallback(() => setView({ scale: 1, pan: REST }), []);
  const panBy = useCallback(
    (dx: number, dy: number) => setView((v) => ({ scale: v.scale, pan: clampPan({ x: v.pan.x + dx, y: v.pan.y + dy }, fitted, box, v.scale) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- same inputs as zoomBy
    [fitted.width, fitted.height, box.width, box.height],
  );

  // Wheel = zoom around the pointer. Non-passive so the page never scrolls
  // behind a zooming picture (React's synthetic wheel listeners are passive).
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP, centred(e.clientX, e.clientY));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy, centred]);

  // ── pointers: one = drag (pan when zoomed, swipe at rest); two = pinch ──
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<{ origin: Point; pan: Point; pinch?: { d0: number; scale0: number } } | null>(null);

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      gesture.current = { origin: midpoint(pts[0]!, pts[1]!), pan, pinch: { d0: distance(pts[0]!, pts[1]!), scale0: view.scale } };
    } else {
      gesture.current = { origin: { x: e.clientX, y: e.clientY }, pan };
    }
    setDragging(true);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!pointers.current.has(e.pointerId) || !gesture.current) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    const g = gesture.current;
    if (g.pinch && pts.length === 2) {
      const d = distance(pts[0]!, pts[1]!);
      const target = clampScale((g.pinch.scale0 * d) / g.pinch.d0, 1, maxScale);
      const mid = midpoint(pts[0]!, pts[1]!);
      setView((v) => zoomAround(v, target / v.scale, centred(mid.x, mid.y), bounds));
      return;
    }
    if (zoomed) {
      const dx = e.clientX - g.origin.x;
      const dy = e.clientY - g.origin.y;
      setView((v) => ({ scale: v.scale, pan: clampPan({ x: g.pan.x + dx, y: g.pan.y + dy }, fitted, box, v.scale) }));
    }
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const g = gesture.current;
    if (g && !g.pinch && pointers.current.size === 1) {
      const intent = swipeIntent(e.clientX - g.origin.x, e.clientY - g.origin.y, view.scale);
      if (intent === 'prev') onSwipePrev?.();
      if (intent === 'next') onSwipeNext?.();
    }
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) {
      gesture.current = null;
      setDragging(false);
    } else {
      // A finger lifted mid-pinch: the remaining one continues as a plain drag.
      const rest = [...pointers.current.values()][0]!;
      gesture.current = { origin: rest, pan };
    }
  };

  const onDoubleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (zoomed) reset();
    else zoomBy(CLOSE_LOOK, centred(e.clientX, e.clientY));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case '+':
      case '=':
        e.preventDefault();
        zoomBy(ZOOM_STEP);
        break;
      case '-':
      case '_':
        e.preventDefault();
        zoomBy(1 / ZOOM_STEP);
        break;
      case '0':
        e.preventDefault();
        reset();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (zoomed) panBy(PAN_STEP_PX, 0);
        else onSwipePrev?.();
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (zoomed) panBy(-PAN_STEP_PX, 0);
        else onSwipeNext?.();
        break;
      case 'ArrowUp':
        if (zoomed) {
          e.preventDefault();
          panBy(0, PAN_STEP_PX);
        }
        break;
      case 'ArrowDown':
        if (zoomed) {
          e.preventDefault();
          panBy(0, -PAN_STEP_PX);
        }
        break;
      case 'Enter':
      case ' ':
        // A button's activation keys are the close-look toggle, like a double-tap.
        e.preventDefault();
        if (zoomed) reset();
        else zoomBy(CLOSE_LOOK);
        break;
      default:
    }
  };

  const controlClass = 'text-[var(--color-on-stage)] hover:bg-[var(--color-on-stage)]/15';

  return (
    <div ref={boxRef} className={`relative h-full w-full overflow-hidden ${className}`}>
      <p id={instructionsId} className="sr-only">
        {instructions}
      </p>
      {/* The gesture surface is the picture's own control — a composite-internal
          button (the DataTable-row posture: a bespoke frame the Button primitive
          would not fit), so focus, Tab and the keyboard come native. */}
      <button
        type="button"
        aria-label={alt}
        aria-describedby={instructionsId}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        onKeyDown={onKeyDown}
        className={
          'flex h-full w-full touch-none select-none items-center justify-center overflow-hidden outline-none focus-ring-inset ' +
          (zoomed ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in')
        }
      >
        <img
          src={src}
          alt=""
          draggable={false}
          onLoad={(e) => setNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
          onError={onError}
          style={{
            width: fitted.width || undefined,
            height: fitted.height || undefined,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${view.scale})`,
            transition: dragging ? 'none' : 'transform var(--duration-fast) ease-out',
          }}
          className="max-h-full max-w-full object-contain"
        />
      </button>
      {/* The non-pointer path to every gesture — bottom end, on the stage. */}
      <div className="absolute bottom-3 end-3 flex items-center gap-1 rounded-md bg-[var(--color-bg-stage)]/70 p-1">
        <IconButton variant="ghost" aria-label={zoomOutLabel} disabled={!zoomed} className={controlClass} onClick={() => zoomBy(1 / ZOOM_STEP)}>
          <icons.zoomOut aria-hidden size={18} />
        </IconButton>
        <IconButton variant="ghost" aria-label={zoomInLabel} disabled={view.scale >= maxScale} className={controlClass} onClick={() => zoomBy(ZOOM_STEP)}>
          <icons.zoomIn aria-hidden size={18} />
        </IconButton>
        <IconButton variant="ghost" aria-label={resetLabel} disabled={!zoomed} className={controlClass} onClick={reset}>
          <icons.collapse aria-hidden size={18} />
        </IconButton>
      </div>
    </div>
  );
}
